import React, { useState } from 'react';
import { 
  Activity, Heart, Shield, HeartPulse, Droplet, Pill, Wind, Bone, Brain, Syringe,
  ArrowRight, Search, Upload, UserCheck, DollarSign, CreditCard, ShieldCheck, Plane,
  MessageCircle, Phone, Star, TrendingUp, Award, Users, FileCheck, CheckCircle2,
  Clock, Package, CalendarDays, TrendingDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  diseaseCategories,
  costComparisonData,
  trustIndicators,
  testimonials,
  howToOrderSteps,
  paymentGatewayInfo,
  expertConsultationInfo,
  companyLegacy
} from '../data/pharmacyData';

const PharmacyHomePage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    phone: '',
    condition: '',
    message: ''
  });

  const iconMap = {
    Activity, Heart, Shield, HeartPulse, Droplet, Pill, Wind, Bone, Brain, Syringe,
    Search, Upload, UserCheck, DollarSign, CreditCard, ShieldCheck, Plane
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/919876543210', '_blank');
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    navigate(`/category/${categoryId}`);
  };

  const handlePrescriptionUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPrescriptionFile(file);
      toast.success(`Prescription "${file.name}" uploaded successfully!`);
    }
  };

  const handleConsultationSubmit = (e) => {
    e.preventDefault();
    console.log('Consultation request:', consultationForm, prescriptionFile);
    toast.success('Consultation request submitted! Our expert will contact you within 4 hours.');
    setConsultationForm({ name: '', email: '', phone: '', condition: '', message: '' });
    setPrescriptionFile(null);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md border-b border-red-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-2xl">M</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">MediSeller</h1>
                <p className="text-xs text-gray-600 font-medium">Affordable Life-Saving Medications</p>
              </div>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center space-x-8">
              <button onClick={() => document.querySelector('#categories')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-700 hover:text-red-600 font-medium transition-colors">Browse Diseases</button>
              <button onClick={() => document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-700 hover:text-red-600 font-medium transition-colors">How It Works</button>
              <button onClick={() => document.querySelector('#savings')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-700 hover:text-red-600 font-medium transition-colors">Cost Savings</button>
              <button onClick={() => document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-700 hover:text-red-600 font-medium transition-colors">Get Consultation</button>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <Button onClick={handleWhatsApp} className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-lg font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all">
                <MessageCircle className="w-4 h-4" />
                <span>WhatsApp</span>
              </Button>
              <Button onClick={() => document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' })} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all">
                <Phone className="w-4 h-4" />
                <span>Get Expert Help</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-red-50 via-white to-red-50 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(220, 38, 38) 1px, transparent 0)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Main Hero Content */}
            <div className="text-center space-y-8 mb-16">
              <div className="inline-flex items-center space-x-2 bg-white rounded-full px-6 py-3 shadow-lg border-2 border-red-200">
                <Award className="w-5 h-5 text-red-600" />
                <span className="text-sm font-bold text-gray-800">45 Years Trusted | 50,000+ Patients Worldwide</span>
              </div>

              <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight">
                Save <span className="text-red-600">Up to 75%</span> on<br />
                Life-Saving Medications
              </h1>

              <p className="text-xl lg:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                Access authentic generic medications for <span className="font-bold text-red-600">Chronic & Terminal Diseases</span>.
                Licensed pharmacy serving 30+ countries with express delivery.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
                <Button onClick={() => document.querySelector('#categories')?.scrollIntoView({ behavior: 'smooth' })} className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-lg rounded-xl font-bold shadow-2xl hover:shadow-red-300 hover:scale-105 transition-all flex items-center justify-center space-x-3">
                  <span>Browse Medications</span>
                  <ArrowRight className="w-6 h-6" />
                </Button>
                <Button onClick={() => document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' })} variant="outline" className="border-3 border-red-600 text-red-600 hover:bg-red-50 px-10 py-6 text-lg rounded-xl font-bold hover:scale-105 transition-all flex items-center justify-center space-x-3">
                  <Upload className="w-6 h-6" />
                  <span>Upload Prescription</span>
                </Button>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-red-100 hover:scale-105 transition-transform">
                <p className="text-4xl font-bold text-red-600 mb-2">{trustIndicators.yearsInBusiness}+</p>
                <p className="text-gray-700 font-semibold text-sm">Years of Excellence</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-red-100 hover:scale-105 transition-transform">
                <p className="text-4xl font-bold text-red-600 mb-2">{trustIndicators.patientsServed}</p>
                <p className="text-gray-700 font-semibold text-sm">Patients Served</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-red-100 hover:scale-105 transition-transform">
                <p className="text-4xl font-bold text-red-600 mb-2">{trustIndicators.averageSavings}</p>
                <p className="text-gray-700 font-semibold text-sm">Average Savings</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-red-100 hover:scale-105 transition-transform">
                <p className="text-4xl font-bold text-red-600 mb-2">{trustIndicators.satisfactionRate}</p>
                <p className="text-gray-700 font-semibold text-sm">Satisfaction Rate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disease Categories Section */}
      <section id="categories" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Browse by <span className="text-red-600">Disease Category</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select your condition to view authentic generic medications with up to 75% savings
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {diseaseCategories.map((category) => {
              const Icon = iconMap[category.icon];
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategorySelect(category.id)}
                  className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-gray-200 hover:border-red-400 relative overflow-hidden"
                >
                  {/* Background Image */}
                  {category.image && (
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300">
                      <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                    </div>
                  )}

                  {/* Type Badge */}
                  <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold ${
                    category.type === 'terminal' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {category.type === 'terminal' ? 'Terminal' : 'Chronic'}
                  </div>

                  <div className="relative z-10">
                    {/* Icon */}
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-transform shadow-lg">
                      <Icon className="w-8 h-8 text-white" />
                    </div>

                    {/* Category Name */}
                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">
                      {category.name}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {category.description}
                    </p>

                    {/* Stats */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Products Available:</span>
                        <span className="font-bold text-gray-900">{category.productCount}+</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Avg. Savings:</span>
                        <span className="font-bold text-green-600">{category.avgSavings}</span>
                      </div>
                    </div>

                    {/* Common Medications */}
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">Popular medications:</p>
                      <div className="flex flex-wrap gap-1">
                        {category.commonMeds.slice(0, 2).map((med, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                            {med}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="mt-4 flex items-center text-red-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                      <span>View Medications</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cost Savings Comparison Section */}
      <section id="savings" className="py-20 bg-gradient-to-br from-red-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <div className="inline-flex items-center space-x-2 bg-white rounded-full px-6 py-3 shadow-lg border-2 border-red-200 mb-4">
                <TrendingDown className="w-5 h-5 text-green-600" />
                <span className="text-sm font-bold text-gray-800">Real Cost Comparisons</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
                US Patented vs <span className="text-red-600">Indian Generic</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See exactly how much you can save on your medications with our authentic generic alternatives
              </p>
            </div>

            {/* Comparison Cards */}
            <div className="space-y-6">
              {costComparisonData.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden hover:shadow-2xl transition-all">
                  <div className="grid md:grid-cols-12 gap-0">
                    {/* Category */}
                    <div className="md:col-span-2 bg-gradient-to-br from-red-600 to-red-700 p-6 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-white font-bold text-lg">{item.category}</p>
                      </div>
                    </div>

                    {/* Medication */}
                    <div className="md:col-span-3 p-6 border-r border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">Medication</p>
                      <p className="font-bold text-gray-900 text-lg">{item.medication}</p>
                    </div>

                    {/* US Price */}
                    <div className="md:col-span-2 p-6 bg-red-50 border-r border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">US Price</p>
                      <p className="font-bold text-red-600 text-2xl">{item.usPrice}</p>
                      <p className="text-xs text-gray-500">per month</p>
                    </div>

                    {/* Indian Price */}
                    <div className="md:col-span-2 p-6 bg-green-50 border-r border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">Our Price</p>
                      <p className="font-bold text-green-600 text-2xl">{item.indianPrice}</p>
                      <p className="text-xs text-gray-500">per month</p>
                    </div>

                    {/* Savings */}
                    <div className="md:col-span-3 p-6 bg-gradient-to-r from-green-50 to-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-2">You Save</p>
                          <p className="font-bold text-green-600 text-2xl">{item.savings}</p>
                          <p className="text-xs text-gray-500">per month</p>
                        </div>
                        <div className="text-right">
                          <div className="bg-green-600 text-white rounded-full px-4 py-2 font-bold text-lg">
                            {item.savingsPercent}
                          </div>
                          <p className="text-xs text-gray-600 mt-2">Annual: {item.annualUSSavings}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 text-white shadow-2xl">
                <h3 className="text-3xl font-bold mb-4">Ready to Start Saving?</h3>
                <p className="text-xl text-red-100 mb-6">Upload your prescription and get a personalized cost comparison</p>
                <Button onClick={() => document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' })} className="bg-white text-red-600 hover:bg-gray-100 px-10 py-4 text-lg rounded-xl font-bold shadow-xl hover:scale-105 transition-all">
                  Get Your Quote Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Real <span className="text-red-600">Patient Stories</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from patients who transformed their lives with affordable, authentic medications
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-gradient-to-br from-white to-red-50 rounded-2xl p-8 shadow-xl border-2 border-red-100 hover:shadow-2xl transition-all">
                <div className="flex items-start space-x-4 mb-6">
                  <img src={testimonial.image} alt={testimonial.name} className="w-20 h-20 rounded-full border-4 border-white shadow-lg" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-xl font-bold text-gray-900">{testimonial.name}, {testimonial.age}</h4>
                        <p className="text-sm text-gray-600">{testimonial.country}</p>
                      </div>
                      {testimonial.verified && (
                        <div className="flex items-center space-x-1 bg-green-100 px-3 py-1 rounded-full">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                          <span className="text-xs text-green-700 font-bold">Verified</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 mt-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-3 border border-red-200">
                      <p className="text-xs text-gray-600">Condition</p>
                      <p className="font-bold text-gray-900 text-sm">{testimonial.condition}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-red-200">
                      <p className="text-xs text-gray-600">Medication</p>
                      <p className="font-bold text-gray-900 text-sm">{testimonial.medication}</p>
                    </div>
                  </div>
                </div>

                <p className="text-gray-700 leading-relaxed italic mb-6">
                  "{testimonial.testimonial}"
                </p>

                <div className="flex items-center justify-between pt-6 border-t-2 border-red-200">
                  <div>
                    <p className="text-sm text-gray-600">Total Savings</p>
                    <p className="text-3xl font-bold text-green-600">{testimonial.savings}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Treatment Duration</p>
                    <p className="font-bold text-gray-900">{testimonial.treatmentDuration}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gradient-to-br from-red-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              How <span className="text-red-600">It Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, secure, and transparent process from prescription to delivery
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="relative">
              {/* Connection Line */}
              <div className="hidden lg:block absolute top-20 left-0 right-0 h-1 bg-gradient-to-r from-red-200 via-red-600 to-red-200"></div>

              <div className="grid md:grid-cols-7 gap-6">
                {howToOrderSteps.map((step, index) => {
                  const Icon = iconMap[step.icon];
                  return (
                    <div key={step.step} className="relative">
                      <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-200 hover:shadow-2xl hover:-translate-y-2 transition-all relative z-10">
                        {/* Step Number */}
                        <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                          {step.step}
                        </div>

                        {/* Icon */}
                        <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center mb-4 mx-auto">
                          <Icon className="w-8 h-8 text-red-600" />
                        </div>

                        {/* Title */}
                        <h3 className="text-center font-bold text-gray-900 mb-3 text-sm">{step.title}</h3>

                        {/* Description */}
                        <p className="text-center text-xs text-gray-600 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Legacy & Trust Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
                <span className="text-red-600">45 Years</span> of Trust & Excellence
              </h2>
              <p className="text-xl text-gray-600">
                Our journey from 1980 to serving 50,000+ patients globally
              </p>
            </div>

            {/* Timeline */}
            <div className="relative mb-16">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-red-200 via-red-600 to-red-200"></div>
              
              <div className="space-y-12">
                {companyLegacy.milestone.map((milestone, index) => (
                  <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className="w-5/12"></div>
                    <div className="w-2/12 flex justify-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center text-white font-bold shadow-xl z-10">
                        {milestone.year}
                      </div>
                    </div>
                    <div className="w-5/12">
                      <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-100 hover:shadow-xl transition-all">
                        <p className="text-gray-900 font-semibold">{milestone.event}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications & Partnerships */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <ShieldCheck className="w-8 h-8 text-red-600 mr-3" />
                  Certifications
                </h3>
                <div className="space-y-3">
                  {companyLegacy.certifications.map((cert, idx) => (
                    <div key={idx} className="flex items-center space-x-3 bg-white rounded-lg p-3 border border-red-200">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-gray-900">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Users className="w-8 h-8 text-red-600 mr-3" />
                  Trusted Partnerships
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {companyLegacy.partnerships.map((partner, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 border border-red-200 text-center">
                      <span className="font-semibold text-gray-900 text-sm">{partner}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prescription Upload & Expert Consultation Section */}
      <section id="consultation" className="py-20 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-12 text-white">
              <h2 className="text-4xl lg:text-5xl font-bold">
                Upload Prescription & Get Expert Consultation
              </h2>
              <p className="text-xl text-red-100">
                Our licensed pharmacists will review your prescription and provide personalized guidance
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Consultation Form */}
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Request Consultation</h3>
                <form onSubmit={handleConsultationSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-semibold">Full Name *</Label>
                    <Input
                      id="name"
                      value={consultationForm.name}
                      onChange={(e) => setConsultationForm({...consultationForm, name: e.target.value})}
                      required
                      className="mt-2 h-12"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-semibold">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={consultationForm.email}
                      onChange={(e) => setConsultationForm({...consultationForm, email: e.target.value})}
                      required
                      className="mt-2 h-12"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-semibold">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={consultationForm.phone}
                      onChange={(e) => setConsultationForm({...consultationForm, phone: e.target.value})}
                      required
                      className="mt-2 h-12"
                      placeholder="+1 234 567 8900"
                    />
                  </div>

                  <div>
                    <Label htmlFor="condition" className="text-gray-700 font-semibold">Medical Condition *</Label>
                    <Input
                      id="condition"
                      value={consultationForm.condition}
                      onChange={(e) => setConsultationForm({...consultationForm, condition: e.target.value})}
                      required
                      className="mt-2 h-12"
                      placeholder="e.g., Chronic Myeloid Leukemia"
                    />
                  </div>

                  <div>
                    <Label htmlFor="prescription" className="text-gray-700 font-semibold">Upload Prescription *</Label>
                    <div className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-red-400 transition-colors cursor-pointer">
                      <input
                        type="file"
                        id="prescription"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handlePrescriptionUpload}
                        className="hidden"
                      />
                      <label htmlFor="prescription" className="cursor-pointer flex flex-col items-center">
                        <Upload className="w-12 h-12 text-red-600 mb-3" />
                        <span className="font-semibold text-gray-900">Click to upload prescription</span>
                        <span className="text-sm text-gray-600 mt-1">PDF, JPG, PNG (Max 5MB)</span>
                        {prescriptionFile && (
                          <span className="mt-3 text-sm text-green-600 font-semibold">
                            ✓ {prescriptionFile.name}
                          </span>
                        )}
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-semibold">Additional Information</Label>
                    <Textarea
                      id="message"
                      value={consultationForm.message}
                      onChange={(e) => setConsultationForm({...consultationForm, message: e.target.value})}
                      className="mt-2"
                      rows={4}
                      placeholder="Tell us about your requirements, current medications, etc."
                    />
                  </div>

                  <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all">
                    Submit for Expert Review
                  </Button>

                  <p className="text-xs text-gray-600 text-center">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Response within 4 hours | Consultation available 24/7
                  </p>
                </form>
              </div>

              {/* Expert Info */}
              <div className="space-y-6">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border-2 border-white/20 text-white">
                  <h3 className="text-2xl font-bold mb-6">Why Consult Our Experts?</h3>
                  <div className="space-y-4">
                    {expertConsultationInfo.consultationTypes.map((type, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                        <span className="font-semibold">{type}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-2xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Our Expert Pharmacists</h3>
                  <div className="space-y-4">
                    {expertConsultationInfo.experts.map((expert, idx) => (
                      <div key={idx} className="border-l-4 border-red-600 pl-4 py-2">
                        <p className="font-bold text-gray-900">{expert.name}</p>
                        <p className="text-sm text-gray-600">{expert.qualification}</p>
                        <p className="text-sm text-red-600 font-semibold">{expert.specialty}</p>
                        <p className="text-xs text-gray-500">{expert.experience} experience</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Gateway Information */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-4xl font-bold text-gray-900">
                Secure <span className="text-red-600">Payment Options</span>
              </h2>
              <p className="text-xl text-gray-600">
                Multiple payment methods with bank-level security
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <CreditCard className="w-12 h-12 text-red-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">Payment Methods</h3>
                <div className="space-y-2">
                  {paymentGatewayInfo.methods.map((method, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-gray-700">{method}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <ShieldCheck className="w-12 h-12 text-red-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">Security Features</h3>
                <div className="space-y-2">
                  {paymentGatewayInfo.security.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <DollarSign className="w-12 h-12 text-red-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">Additional Features</h3>
                <div className="space-y-2">
                  {paymentGatewayInfo.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gradient-to-r from-green-50 to-white rounded-2xl p-8 border-2 border-green-200">
              <div className="flex items-center justify-center space-x-8">
                <ShieldCheck className="w-16 h-16 text-green-600" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    PCI DSS Level 1 Certified Payment Gateway
                  </h3>
                  <p className="text-gray-600">
                    Your payment information is encrypted and secure. We never store your card details.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Start Your Journey to Affordable Healthcare Today
            </h2>
            <p className="text-xl text-red-100">
              Join 50,000+ patients who've saved millions on life-saving medications
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => document.querySelector('#categories')?.scrollIntoView({ behavior: 'smooth' })} className="bg-white text-red-600 hover:bg-gray-100 px-10 py-4 text-lg rounded-xl font-bold shadow-2xl hover:scale-105 transition-all">
                Browse Medications
              </Button>
              <Button onClick={handleWhatsApp} className="bg-green-600 hover:bg-green-700 text-white px-10 py-4 text-lg rounded-xl font-bold shadow-2xl hover:scale-105 transition-all flex items-center justify-center space-x-3">
                <MessageCircle className="w-5 h-5" />
                <span>Chat on WhatsApp</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">M</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">MediSeller</h3>
                  <p className="text-xs text-gray-400">Since 1980</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Trusted online pharmacy providing authentic generic medications for chronic and terminal diseases.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => document.querySelector('#categories')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white text-sm transition-colors">Browse Diseases</button></li>
                <li><button onClick={() => document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white text-sm transition-colors">How It Works</button></li>
                <li><button onClick={() => document.querySelector('#savings')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white text-sm transition-colors">Cost Savings</button></li>
                <li><button onClick={() => document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white text-sm transition-colors">Get Consultation</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Disease Categories</h4>
              <ul className="space-y-2">
                {diseaseCategories.slice(0, 6).map(cat => (
                  <li key={cat.id}><button onClick={() => handleCategorySelect(cat.id)} className="text-gray-400 hover:text-white text-sm transition-colors">{cat.name}</button></li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Contact Us</h4>
              <div className="space-y-3">
                <Button onClick={handleWhatsApp} className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <p className="text-sm text-gray-400">Available 24/7 for consultations</p>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-400 text-sm">© {new Date().getFullYear()} MediSeller. All rights reserved. | Licensed International Pharmacy</p>
          </div>
        </div>
      </footer>

      {/* Sticky WhatsApp Button */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-8 right-8 bg-green-600 hover:bg-green-700 text-white p-5 rounded-full shadow-2xl hover:scale-110 transition-all z-50 flex items-center space-x-2 group"
      >
        <MessageCircle className="w-7 h-7" />
        <span className="hidden group-hover:inline-block pr-3 font-bold text-lg">Chat with Expert</span>
      </button>
    </div>
  );
};

export default PharmacyHomePage;